import { useEffect, useState, type CSSProperties } from 'react'
import { avatarColor } from './avatarColor'

const FALLBACK = '#5865f2' // Same as the initials avatar in profile cards.
const colours = new Map<string, Promise<string | null>>()

function loadColour(src: string): Promise<string | null> {
  const cached = colours.get(src)
  if (cached) return cached
  const promise = new Promise<string | null>((resolve) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    const finish = (colour: string | null, failed = false) => {
      clearTimeout(timeout)
      image.onload = image.onerror = null
      if (failed) colours.delete(src) // A later card may retry a failed network request.
      resolve(colour)
    }
    const timeout = setTimeout(() => finish(null, true), 10000)
    image.onerror = () => finish(null, true)
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = canvas.height = 32
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx || !image.naturalWidth || !image.naturalHeight) { finish(null, true); return }
        // Match the centred cover crop and circular mask of the displayed avatar.
        ctx.beginPath()
        ctx.arc(16, 16, 16, 0, Math.PI * 2)
        ctx.clip()
        const side = Math.min(image.naturalWidth, image.naturalHeight)
        ctx.drawImage(image, (image.naturalWidth - side) / 2, (image.naturalHeight - side) / 2, side, side, 0, 0, 32, 32)
        finish(avatarColor(ctx.getImageData(0, 0, 32, 32).data))
      } catch { finish(null, true) } // Unreadable/CORS images use the initials colour.
    }
    image.src = src
  })
  if (colours.size >= 256) colours.delete(colours.keys().next().value!)
  colours.set(src, promise)
  return promise
}

/** Custom banners take priority. Missing banners follow the avatar actually shown in this card. */
export function useProfileBanner(banner: string | undefined, avatar: string | undefined): CSSProperties {
  const src = banner ? undefined : avatar
  const [result, setResult] = useState<{ src: string; colour: string | null } | null>(null)
  useEffect(() => {
    if (!src) return
    let active = true
    void loadColour(src).then((colour) => { if (active) setResult({ src, colour }) })
    return () => { active = false }
  }, [src])
  return {
    backgroundColor: (src && result?.src === src ? result.colour : null) ?? FALLBACK,
    backgroundImage: banner ? `url(${banner})` : 'none',
  }
}
